/**
 * ScopeGovernancePanel - Inspector panel for scope governance features
 * Phase 1: Decisions, Risks, Drift, Documents, Category/Checklist
 */

import React, { useState, useEffect } from 'react';
import {
  FileCheck, AlertTriangle, FileText, Layers, ChevronDown, ChevronRight,
  Link2, Unlink, Plus, Download, Upload, Trash2, Check, X, ExternalLink,
  GitCompare, Target, Clock, User, Sparkles, RefreshCw, Info
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useProductMode } from '../context/ProductModeContext';
import { useSupportContext } from '../context/SupportContext';
import { toast } from 'sonner';
import { trackEvent, TELEMETRY_EVENTS } from '../utils/telemetry';
import DocumentVersionPanel from './DocumentVersionPanel';
import SignoffsPanel from './SignoffsPanel';

// Impact type options
const IMPACT_TYPES = [
  { value: 'add', label: 'Add Scope', color: 'bg-green-100 text-green-700' },
  { value: 'remove', label: 'Remove Scope', color: 'bg-red-100 text-red-700' },
  { value: 'clarify', label: 'Clarify', color: 'bg-blue-100 text-blue-700' },
  { value: 'defer', label: 'Defer', color: 'bg-amber-100 text-amber-700' },
  { value: 'quality', label: 'Quality', color: 'bg-purple-100 text-purple-700' },
  { value: 'non-functional', label: 'Non-Functional', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'ops', label: 'Ops/Handover', color: 'bg-gray-100 text-gray-700' },
];

// Category options
const CATEGORIES = [
  { value: 'functional', label: 'Functional', icon: Target },
  { value: 'non-functional', label: 'Non-Functional', icon: FileCheck },
  { value: 'ops-handover', label: 'Ops/Handover', icon: Clock },
];

// Severity colors
const SEVERITY_COLORS = {
  low: 'bg-blue-100 text-blue-700 border-blue-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  high: 'bg-red-100 text-red-700 border-red-200',
  critical: 'bg-red-200 text-red-800 border-red-300',
};

/**
 * Decisions Sub-Panel
 */
const DecisionsSubPanel = ({ scopeId, tenantId, decisions, onRefresh }) => {
  const [expanded, setExpanded] = useState(true);
  const { openSupportDrawer } = useSupportContext();
  
  const handleUnlink = async (decisionId) => {
    try {
      await api.unlinkDecisionFromScope(decisionId, scopeId, tenantId);
      trackEvent(TELEMETRY_EVENTS.SCOPING_DECISION_LINK_REMOVED, { scopeId, decisionId });
      toast.success('Decision unlinked');
      onRefresh?.();
    } catch (err) {
      toast.error('Failed to unlink decision');
    }
  };
  
  const handleProposeBaseline = async (decisionId, decisionTitle) => {
    try {
      // Would need projectId - for now show support AI
      trackEvent(TELEMETRY_EVENTS.SCOPING_BASELINE_PROPOSE_FROM_DECISION, { scopeId, decisionId });
      openSupportDrawer?.({
        tab: 'advisory',
        initialMessage: `Help me create a baseline update based on decision "${decisionTitle}"`
      });
    } catch (err) {
      toast.error('Failed to propose baseline');
    }
  };
  
  return (
    <div className="border border-[var(--clr-border-light)] rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 bg-[var(--clr-surface-subtle)] hover:bg-[var(--clr-surface-hover)] transition-colors"
      >
        <div className="flex items-center gap-2">
          <FileCheck className="w-4 h-4 text-[var(--clr-brand-primary)]" />
          <span className="text-sm font-medium">Decisions</span>
          {decisions.length > 0 && (
            <span className="px-1.5 py-0.5 text-xs bg-[var(--clr-brand-primary)]/10 text-[var(--clr-brand-primary)] rounded-full">
              {decisions.length}
            </span>
          )}
        </div>
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      
      {expanded && (
        <div className="p-3 space-y-2">
          {decisions.length === 0 ? (
            <p className="text-xs text-[var(--clr-text-muted)] text-center py-2">
              No decisions linked to this scope item
            </p>
          ) : (
            decisions.map((decision) => (
              <div
                key={decision.id}
                className="flex items-center justify-between p-2 bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)]"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--clr-text-primary)] truncate">
                    {decision.title}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-1.5 py-0.5 text-xs rounded ${
                      IMPACT_TYPES.find(t => t.value === decision.impact_type)?.color || 'bg-gray-100'
                    }`}>
                      {decision.impact_type}
                    </span>
                    <span className="text-xs text-[var(--clr-text-muted)]">
                      {decision.status}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleProposeBaseline(decision.id, decision.title)}
                    className="p-1.5 text-[var(--clr-text-muted)] hover:text-[var(--clr-brand-primary)] hover:bg-[var(--clr-surface-hover)] rounded transition-colors"
                    title="Propose Baseline Update"
                  >
                    <GitCompare className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleUnlink(decision.id)}
                    className="p-1.5 text-[var(--clr-text-muted)] hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Unlink Decision"
                  >
                    <Unlink className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Risks Sub-Panel
 */
const RisksSubPanel = ({ scopeId, tenantId, risks, onRefresh }) => {
  const [expanded, setExpanded] = useState(true);
  
  const handleUnlink = async (riskId) => {
    try {
      await api.unlinkRiskFromScope(riskId, scopeId);
      trackEvent(TELEMETRY_EVENTS.SCOPING_RISK_LINK_REMOVED, { scopeId, riskId });
      toast.success('Risk unlinked');
      onRefresh?.();
    } catch (err) {
      toast.error('Failed to unlink risk');
    }
  };
  
  const highSeverityCount = risks.filter(r => r.severity === 'high' || r.severity === 'critical').length;
  
  return (
    <div className="border border-[var(--clr-border-light)] rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 bg-[var(--clr-surface-subtle)] hover:bg-[var(--clr-surface-hover)] transition-colors"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-medium">Risks</span>
          {risks.length > 0 && (
            <span className={`px-1.5 py-0.5 text-xs rounded-full ${
              highSeverityCount > 0 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
            }`}>
              {risks.length}
            </span>
          )}
        </div>
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      
      {expanded && (
        <div className="p-3 space-y-2">
          {risks.length === 0 ? (
            <p className="text-xs text-[var(--clr-text-muted)] text-center py-2">
              No risks linked to this scope item
            </p>
          ) : (
            risks.map((risk) => (
              <div
                key={risk.id}
                className="flex items-center justify-between p-2 bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)]"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--clr-text-primary)] truncate">
                    {risk.title}
                  </p>
                  <span className={`inline-block px-1.5 py-0.5 text-xs rounded border mt-1 ${
                    SEVERITY_COLORS[risk.severity] || SEVERITY_COLORS.medium
                  }`}>
                    {risk.severity}
                  </span>
                </div>
                <button
                  onClick={() => handleUnlink(risk.id)}
                  className="p-1.5 text-[var(--clr-text-muted)] hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  title="Unlink Risk"
                >
                  <Unlink className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Documents Sub-Panel
 */
const DocumentsSubPanel = ({ scopeId, tenantId, docs, onRefresh, canEdit }) => {
  const [expanded, setExpanded] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDoc, setNewDoc] = useState({ name: '', url: '', type: 'url' });
  const { user } = useAuth();
  
  const handleAttach = async () => {
    if (!newDoc.name || !newDoc.url) {
      toast.error('Name and URL are required');
      return;
    }
    
    try {
      await api.attachDocumentToScope(scopeId, {
        name: newDoc.name,
        url: newDoc.url,
        type: newDoc.type,
        uploaded_by: user?.name || user?.email || 'Unknown'
      }, tenantId);
      
      trackEvent(TELEMETRY_EVENTS.SCOPING_DOC_ATTACHED, { scopeId });
      toast.success('Document attached');
      setNewDoc({ name: '', url: '', type: 'url' });
      setShowAddForm(false);
      onRefresh?.();
    } catch (err) {
      toast.error('Failed to attach document');
    }
  };
  
  const handleRemove = async (docId) => {
    try {
      await api.removeDocumentFromScope(scopeId, docId, tenantId);
      toast.success('Document removed');
      onRefresh?.();
    } catch (err) {
      toast.error('Failed to remove document');
    }
  };
  
  const handleDownload = (doc) => {
    trackEvent(TELEMETRY_EVENTS.SCOPING_DOC_DOWNLOADED, { scopeId, docId: doc.id });
    window.open(doc.url, '_blank');
  };
  
  return (
    <div className="border border-[var(--clr-border-light)] rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 bg-[var(--clr-surface-subtle)] hover:bg-[var(--clr-surface-hover)] transition-colors"
      >
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[var(--clr-text-muted)]" />
          <span className="text-sm font-medium">Documents</span>
          {docs.length > 0 && (
            <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full">
              {docs.length}
            </span>
          )}
        </div>
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      
      {expanded && (
        <div className="p-3 space-y-2">
          {docs.length === 0 && !showAddForm && (
            <p className="text-xs text-[var(--clr-text-muted)] text-center py-2">
              No documents attached
            </p>
          )}
          
          {docs.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center justify-between p-2 bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)]"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[var(--clr-text-primary)] truncate">
                  {doc.name}
                </p>
                <p className="text-xs text-[var(--clr-text-muted)]">
                  {doc.uploaded_by} • {new Date(doc.uploaded_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleDownload(doc)}
                  className="p-1.5 text-[var(--clr-text-muted)] hover:text-[var(--clr-brand-primary)] hover:bg-[var(--clr-surface-hover)] rounded transition-colors"
                  title="Download/Open"
                >
                  <Download className="w-4 h-4" />
                </button>
                {canEdit && (
                  <button
                    onClick={() => handleRemove(doc.id)}
                    className="p-1.5 text-[var(--clr-text-muted)] hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
          
          {showAddForm ? (
            <div className="p-3 bg-[var(--clr-surface-subtle)] rounded-lg space-y-2">
              <input
                type="text"
                placeholder="Document name"
                value={newDoc.name}
                onChange={(e) => setNewDoc({ ...newDoc, name: e.target.value })}
                className="w-full px-3 py-2 text-sm border rounded-lg"
              />
              <input
                type="url"
                placeholder="URL or file link"
                value={newDoc.url}
                onChange={(e) => setNewDoc({ ...newDoc, url: e.target.value })}
                className="w-full px-3 py-2 text-sm border rounded-lg"
              />
              <select
                value={newDoc.type}
                onChange={(e) => setNewDoc({ ...newDoc, type: e.target.value })}
                className="w-full px-3 py-2 text-sm border rounded-lg"
              >
                <option value="url">URL Link</option>
                <option value="pdf">PDF</option>
                <option value="docx">Word Document</option>
                <option value="xlsx">Excel Spreadsheet</option>
              </select>
              <div className="flex gap-2">
                <button
                  onClick={handleAttach}
                  className="flex-1 px-3 py-2 text-sm bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)]"
                >
                  Attach
                </button>
                <button
                  onClick={() => setShowAddForm(false)}
                  className="px-3 py-2 text-sm text-[var(--clr-text-muted)] hover:bg-[var(--clr-surface-hover)] rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : canEdit && (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full flex items-center justify-center gap-2 p-2 text-sm text-[var(--clr-brand-primary)] hover:bg-[var(--clr-surface-hover)] rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Attach Document
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Category & Checklist Sub-Panel
 */
const CategoryChecklistPanel = ({ scopeId, tenantId, category, checklist, onRefresh, canEdit }) => {
  const [expanded, setExpanded] = useState(false);
  const [localCategory, setLocalCategory] = useState(category);
  const [localChecklist, setLocalChecklist] = useState(checklist);
  const [newItemLabel, setNewItemLabel] = useState('');
  
  const handleCategoryChange = async (newCategory) => {
    try {
      await api.updateScopeCategory(scopeId, newCategory, tenantId);
      setLocalCategory(newCategory);
      toast.success('Category updated');
    } catch (err) {
      toast.error('Failed to update category');
    }
  };
  
  const handleChecklistToggle = async (itemId) => {
    const updated = localChecklist.map(item => 
      item.id === itemId ? { ...item, state: item.state === 'done' ? 'todo' : 'done' } : item
    );
    setLocalChecklist(updated);
    
    try {
      await api.updateScopeChecklist(scopeId, updated, tenantId);
    } catch (err) {
      // Revert on error
      setLocalChecklist(checklist);
      toast.error('Failed to update checklist');
    }
  };
  
  const handleAddItem = async () => {
    if (!newItemLabel.trim()) return;
    
    const newItem = {
      id: `item_${Date.now()}`,
      label: newItemLabel.trim(),
      state: 'todo'
    };
    
    const updated = [...localChecklist, newItem];
    setLocalChecklist(updated);
    setNewItemLabel('');
    
    try {
      await api.updateScopeChecklist(scopeId, updated, tenantId);
    } catch (err) {
      setLocalChecklist(checklist);
      toast.error('Failed to add item');
    }
  };
  
  const completedCount = localChecklist.filter(i => i.state === 'done').length;
  
  return (
    <div className="border border-[var(--clr-border-light)] rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 bg-[var(--clr-surface-subtle)] hover:bg-[var(--clr-surface-hover)] transition-colors"
      >
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-[var(--clr-text-muted)]" />
          <span className="text-sm font-medium">Category & Checklist</span>
          {localChecklist.length > 0 && (
            <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full">
              {completedCount}/{localChecklist.length}
            </span>
          )}
        </div>
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      
      {expanded && (
        <div className="p-3 space-y-3">
          {/* Category selector */}
          <div>
            <label className="block text-xs font-medium text-[var(--clr-text-muted)] mb-1">
              Category
            </label>
            <div className="flex gap-1">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => canEdit && handleCategoryChange(cat.value)}
                  disabled={!canEdit}
                  className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded-lg border transition-colors ${
                    localCategory === cat.value
                      ? 'bg-[var(--clr-brand-primary)] text-white border-[var(--clr-brand-primary)]'
                      : 'bg-[var(--clr-surface-card)] text-[var(--clr-text-secondary)] border-[var(--clr-border-light)] hover:bg-[var(--clr-surface-hover)]'
                  } ${!canEdit ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  <cat.icon className="w-3 h-3" />
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Checklist */}
          <div>
            <label className="block text-xs font-medium text-[var(--clr-text-muted)] mb-1">
              Checklist
            </label>
            <div className="space-y-1">
              {localChecklist.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-2 p-2 bg-[var(--clr-surface-card)] rounded border border-[var(--clr-border-light)]"
                >
                  <button
                    onClick={() => canEdit && handleChecklistToggle(item.id)}
                    disabled={!canEdit}
                    className={`w-5 h-5 rounded border flex items-center justify-center ${
                      item.state === 'done'
                        ? 'bg-green-500 border-green-500 text-white'
                        : 'border-gray-300 hover:border-gray-400'
                    } ${!canEdit ? 'cursor-not-allowed' : ''}`}
                  >
                    {item.state === 'done' && <Check className="w-3 h-3" />}
                  </button>
                  <span className={`text-sm ${item.state === 'done' ? 'line-through text-[var(--clr-text-muted)]' : ''}`}>
                    {item.label}
                  </span>
                </div>
              ))}
              
              {canEdit && (
                <div className="flex gap-1 mt-2">
                  <input
                    type="text"
                    placeholder="Add checklist item..."
                    value={newItemLabel}
                    onChange={(e) => setNewItemLabel(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddItem()}
                    className="flex-1 px-2 py-1.5 text-sm border rounded-lg"
                  />
                  <button
                    onClick={handleAddItem}
                    disabled={!newItemLabel.trim()}
                    className="px-3 py-1.5 text-sm bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] disabled:opacity-50"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Main Scope Governance Panel
 */
const ScopeGovernancePanel = ({ scopeId, scopeName, tenantId }) => {
  const [loading, setLoading] = useState(true);
  const [governance, setGovernance] = useState(null);
  const [error, setError] = useState(null);
  const { getFeature, productMode } = useProductMode();
  const { user, hasPermission } = useAuth();
  
  // Feature flags
  const decisionsEnabled = getFeature('scoping.decisions', false);
  const riskLinksEnabled = getFeature('scoping.riskLinks', false);
  const docsEnabled = getFeature('scoping.docs', false);
  const nfrOpsEnabled = getFeature('scoping.nfrOps', false);
  // Phase 2 flags
  const docVersioningEnabled = getFeature('scoping.docVersioning', false);
  const signoffsEnabled = getFeature('scoping.signoffs', false);
  
  const canEdit = hasPermission?.('scope_edit') ?? true;
  
  const loadGovernance = async () => {
    if (!scopeId || !tenantId) return;
    
    setLoading(true);
    try {
      const res = await api.getScopeGovernance(scopeId, tenantId);
      setGovernance(res.data);
    } catch (err) {
      setError('Failed to load governance data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadGovernance();
  }, [scopeId, tenantId]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-5 h-5 text-[var(--clr-text-muted)] animate-spin" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-4 text-center">
        <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
        <p className="text-sm text-[var(--clr-text-muted)]">{error}</p>
      </div>
    );
  }
  
  // If no features enabled, show info message
  if (!decisionsEnabled && !riskLinksEnabled && !docsEnabled && !nfrOpsEnabled && !docVersioningEnabled && !signoffsEnabled) {
    return (
      <div className="p-4 text-center">
        <Info className="w-8 h-8 text-[var(--clr-text-muted)] mx-auto mb-2" />
        <p className="text-sm text-[var(--clr-text-muted)]">
          Governance features are not enabled for this tenant.
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-3" data-testid="scope-governance-panel">
      {decisionsEnabled && (
        <DecisionsSubPanel
          scopeId={scopeId}
          tenantId={tenantId}
          decisions={governance?.decisions || []}
          onRefresh={loadGovernance}
        />
      )}
      
      {riskLinksEnabled && (
        <RisksSubPanel
          scopeId={scopeId}
          tenantId={tenantId}
          risks={governance?.risks || []}
          onRefresh={loadGovernance}
        />
      )}
      
      {/* Phase 2: Document Versioning (replaces basic docs if enabled) */}
      {docVersioningEnabled ? (
        <DocumentVersionPanel
          scopeId={scopeId}
          tenantId={tenantId}
          canEdit={canEdit}
        />
      ) : docsEnabled && (
        <DocumentsSubPanel
          scopeId={scopeId}
          tenantId={tenantId}
          docs={governance?.docs || []}
          onRefresh={loadGovernance}
          canEdit={canEdit}
        />
      )}
      
      {/* Phase 2: Sign-offs */}
      {signoffsEnabled && (
        <SignoffsPanel
          scopeId={scopeId}
          tenantId={tenantId}
          canEdit={canEdit}
        />
      )}
      
      {nfrOpsEnabled && (
        <CategoryChecklistPanel
          scopeId={scopeId}
          tenantId={tenantId}
          category={governance?.category || 'functional'}
          checklist={governance?.checklist || []}
          onRefresh={loadGovernance}
          canEdit={canEdit}
        />
      )}
    </div>
  );
};

export default ScopeGovernancePanel;
export { DecisionsSubPanel, RisksSubPanel, DocumentsSubPanel, CategoryChecklistPanel };
